import { type FC, forwardRef, type MouseEvent as ReactMouseEvent } from 'react';
import classNames from 'classnames/bind';
import { optionalProp } from '../../core';
import { type IconName, FAIcon, type FAIconProps, IconStyle } from '../../Icons/Icons';
import TextLink, { TextLinkStyle } from '../../TextLink/TextLink';
import { Theme } from '../../types/enums';
import { filterIconTheme, filterTextLinkTheme } from '../../utils/ThemeFilter/ThemeFilter';
import type { NotificationPropType, NotificationTheme } from './types/propsType';
import styles from './Notification.module.scss';

const cx = classNames.bind(styles);

function getNotificationIcon(type: Theme, customIcon?: IconName): IconName | null {
  if (customIcon) {
    return customIcon;
  }
  const iconNames: { [key: string]: IconName } = {
    [Theme.SUCCESS]: 'circle-check',
    [Theme.WARNING]: 'triangle-exclamation',
    [Theme.ERROR]: 'square-exclamation',
    [Theme.ACTION]: 'bolt',
  };

  return iconNames[type] || null;
}

const Notification: FC<NotificationPropType> = forwardRef<HTMLDivElement, NotificationPropType>(
  (
    {
      textLink,
      message,
      description,
      onCloseClick = null,
      theme = Theme.SUCCESS,
      hasNotificationIcon = true,
      customIcon,
      hasBoldText = true,
      zIndex = 4,
      onOpenClick = null,
      ...restProps
    },
    ref,
  ) => {
    const customIconProps = customIcon || ({} as FAIconProps);
    const { iconName, iconStyle = IconStyle.REGULAR, className = '', ...restCustomIconProps } = customIconProps;

    const notificationIconCondition = getNotificationIcon(theme, iconName);
    const handleOnCloseClick = (e: ReactMouseEvent<HTMLSpanElement, MouseEvent>) => {
      if (onCloseClick) {
        onCloseClick(e);
      }
    };
    const handleOnOpenClick = (e: ReactMouseEvent<HTMLSpanElement, MouseEvent>) => {
      if (onOpenClick) {
        onOpenClick(e);
      }
    };
    const CClassname = cx('notification-container', [theme], { 'detailed-notififation': description });
    const CIconClassname = cx('icon', { 'icon-spacing': description }, className);
    return (
      <div
        className={CClassname}
        style={zIndex && zIndex <= 5 ? { zIndex: zIndex } : { zIndex: 4 }}
        {...restProps}
        ref={ref || restProps?.forwardedRef}
      >
        <span className={styles['sub-continer']}>
          {hasNotificationIcon && notificationIconCondition && (
            <FAIcon
              iconName={notificationIconCondition}
              iconStyle={iconStyle}
              className={CIconClassname}
              {...restCustomIconProps}
            />
          )}
          <section data-testid="notification-info-testid">
            <p className={`${!hasBoldText && styles['no-bold-text']}`}>{message}</p>
            {description && <p className={styles['message-deatils']}>{description}</p>}
          </section>
        </span>
        {textLink?.text && (
          <div className={styles['notification-text-link']}>
            <TextLink
              href={textLink.href}
              text={textLink.text}
              {...optionalProp('theme', filterTextLinkTheme(theme))}
              textLinkStyle={TextLinkStyle.UNDERLINE}
            />
          </div>
        )}
        {onCloseClick && (
          <FAIcon
            iconName="xmark"
            iconStyle={IconStyle.REGULAR}
            onClick={handleOnCloseClick}
            data-testid="notification-close-icon"
            {...optionalProp('theme', filterIconTheme(theme))}
          />
        )}
        {onOpenClick && (
          <FAIcon
            iconName={'chevron-right'}
            iconStyle={IconStyle.REGULAR}
            {...optionalProp('theme', filterIconTheme(theme))}
            onClick={handleOnOpenClick}
            data-testid="notification-open-icon"
          />
        )}
      </div>
    );
  },
);
Notification.displayName = 'Notification';

export default Notification;
export type { NotificationPropType, NotificationTheme };
